import React, { createContext, useContext, useReducer } from 'react';

// Initial state
const initialState = {
  // SignUp form data
  firstName: '',
  lastName: '',
  email: '',
  nmlsId: '',
  stateLicense: '',
  termsAccepted: false,
  sponsorId: '',
  referral: '',
  companyId: '',
  selectedOffice: '',

  // Payment form data
  plan: 'hb-lender-starter',
  subscriptionLength: 'monthly',
  paymentMethod: 'bank', // 'bank' or 'card'
  cardNumber: '',
  expiration: '',
  securityCode: '',
  cardFirstName: '',
  cardLastName: '',
  billingAddress: '',
  billingCity: '',
  billingState: '',
  billingZip: '',
  billingCountry: 'US',
  promoCode: '',
  showPromoCode: false,

  // Bank account data
  accountNumber: '',
  accountNumberConfirmation: '',
  routingNumber: '',
  accountType: 'checking', // 'checking' or 'savings'
  nameOnAccount: '',

  // Form validation state
  errors: {},
  isSubmitting: false
};

// Action types
export const FORM_ACTIONS = {
  UPDATE_FIELD: 'UPDATE_FIELD',
  SET_ERRORS: 'SET_ERRORS',
  CLEAR_ERRORS: 'CLEAR_ERRORS',
  SET_SUBMITTING: 'SET_SUBMITTING',
  RESET_FORM: 'RESET_FORM'
};

// Reducer function
const formReducer = (state, action) => {
  switch (action.type) {
    case FORM_ACTIONS.UPDATE_FIELD:
      return {
        ...state,
        [action.field]: action.value
      };

    case FORM_ACTIONS.SET_ERRORS:
      return {
        ...state,
        errors: { ...state.errors, ...action.errors }
      };

    case FORM_ACTIONS.CLEAR_ERRORS:
      return {
        ...state,
        errors: {}
      };

    case FORM_ACTIONS.SET_SUBMITTING:
      return {
        ...state,
        isSubmitting: action.isSubmitting
      };

    case FORM_ACTIONS.RESET_FORM:
      return initialState;

    default:
      return state;
  }
};

// Create context
const FormStateContext = createContext();
const FormDispatchContext = createContext();

// Provider component
export const FormStateProvider = ({ children }) => {
  const [state, dispatch] = useReducer(formReducer, initialState);

  return (
    <FormStateContext.Provider value={state}>
      <FormDispatchContext.Provider value={dispatch}>
        {children}
      </FormDispatchContext.Provider>
    </FormStateContext.Provider>
  );
};

// Custom hooks
export const useFormState = () => {
  const context = useContext(FormStateContext);
  if (!context) {
    throw new Error('useFormState must be used within a FormStateProvider');
  }
  return context;
};

export const useFormDispatch = () => {
  const context = useContext(FormDispatchContext);
  if (!context) {
    throw new Error('useFormDispatch must be used within a FormStateProvider');
  }
  return context;
};

// Helper functions
export const updateField = (dispatch, field, value) => {
  dispatch({
    type: FORM_ACTIONS.UPDATE_FIELD,
    field,
    value
  });
};

export const setErrors = (dispatch, errors) => {
  dispatch({
    type: FORM_ACTIONS.SET_ERRORS,
    errors
  });
};

export const clearErrors = (dispatch) => {
  dispatch({
    type: FORM_ACTIONS.CLEAR_ERRORS
  });
};

export const setSubmitting = (dispatch, isSubmitting) => {
  dispatch({
    type: FORM_ACTIONS.SET_SUBMITTING,
    isSubmitting
  });
};

export const resetForm = (dispatch) => {
  dispatch({
    type: FORM_ACTIONS.RESET_FORM
  });
};
